import React, { useState, useEffect } from "react";
import {
  Typography,
  Container,
  Grid,
  TextField,
  Button,
  Box,
  ListItemText,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Footer from '../Materials/Footer';
import { useAuth } from '../../context/AuthContext';
import BurgerBar from '../Materials/BurgerBar';


export default function ProjectForm() {
  const { user } = useAuth();
  const [projectName, setProjectName] = useState("");
  const [acronymName, setAcronymName] = useState("");
  const [keywords, setKeywords] = useState("");
  const [objectives, setObjectives] = useState("");
  const [description, setDescription] = useState("");
  const [innovationLevel, setInnovationLevel] = useState("");
  const [budget, setBudget] = useState("");
  const [adviceId, setAdviceId] = useState("");
  const [advices, setAdvices] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({
      ...prev,
      open: false
    }));
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch('https://api.hub4food.colab4food.com/api/users');
        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }
        const data = await response.json();

        const formattedUsers = data.map(user => ({
          id: user._id || user.id,
          name: user.name,
          email: user.email,
        }));
        
        setUsers(formattedUsers);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    const fetchAdvices = async () => {
      try {
        const response = await fetch('https://api.hub4food.colab4food.com/api/advices');
        if (response.ok) {
          const data = await response.json();
          setAdvices(data);
        } else {
          console.error("Failed to fetch advices:", response.statusText);
        }
      } catch (error) {
        console.error("Error fetching advices:", error);
      }
    };

    fetchUsers();
    fetchAdvices();
  }, []);

  const handleSubmit = async () => {
    if (!user) {
      showNotification(
        'User not logged in. Please log in to create an idea.',
        'warning'
      );
      return;
    }
  
    const ideaData = {
      project_name: projectName || "",
      acronym_name: acronymName || "",
      keywords: keywords || "",
      objectives: objectives || "",
      description: description || "",
      innovation_level: innovationLevel || "",
      budget: parseFloat(budget) || 0,
      user_id: user.userId,
      advice_id: adviceId || null,
      co_authors: selectedEmails.map(user => user.id), 
    };
  
    try {
      const response = await fetch('https://api.hub4food.colab4food.com/api/ideas', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(ideaData),
      });
  
      if (response.ok) {
        const result = await response.json();
        console.log("Idea created successfully:", result);

        showNotification(
          'Idea created successfully!',
          'success'
        );

        setTimeout(() => {
          navigate('/listIdea');
        }, 1500);
      } else {
        console.error("Failed to create idea:", response.statusText);

        showNotification(
          'Failed to create idea. Please try again.',
          'error'
        );
      }
    } catch (error) {
      console.error("Error creating idea:", error);

      showNotification(
        'An error occurred while creating the idea.',
        'error'
      );
    }
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setOpenDrawer(!openDrawer);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setOpenDrawer(false);
  };

  const handleExit = () => {
    localStorage.removeItem("user");
    navigate("/");
  };

  const handleEmailChange = (event) => {
    setSelectedEmails(event.target.value);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      {/* Navbar */}
      <BurgerBar />

      {/* Conteúdo Principal */}
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Create Idea
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Project Name"
              fullWidth
              required
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Acronym Name"
              fullWidth
              required
              value={acronymName}
              onChange={(e) => setAcronymName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Keywords"
              fullWidth
              required
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Budget"
              fullWidth
              required
              type="number"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label="Calls"
              fullWidth
              value={adviceId}
              onChange={(e) => setAdviceId(e.target.value)}
            >
              {advices.map((advice) => (
                <MenuItem key={advice.id} value={advice.id}>
                  {advice.title}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          {/* Novo campo: Select emails */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Partners</InputLabel>
              <Select
                multiple
                value={selectedEmails}
                onChange={handleEmailChange}
                label="Select emails to share"
                renderValue={(selected) => selected.map(user => user.email).join(', ')}
              >
                {loading ? (
                  <MenuItem disabled>Loading users...</MenuItem>
                ) : error ? (
                  <MenuItem disabled>Error loading users</MenuItem>
                ) : (
                  users.flatMap(user => 
                    <MenuItem key={user.id} value={user}>
                        <Checkbox checked={selectedEmails.includes(user)} />
                        <ListItemText primary={user.email} />
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="SMART Project Objectives"
              fullWidth
              required
              multiline
              rows={4}
              value={objectives}
              onChange={(e) => setObjectives(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Description"
              fullWidth
              required
              multiline
              rows={6}
              value={description}
              onChange={(e) => {
                if (e.target.value.length <= 200) {
                  setDescription(e.target.value);
                }
              }}
              inputProps={{
                maxLength: 200,
              }}
              helperText={`${description.length}/200 characters`}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Innovative Level"
              fullWidth
              required
              multiline
              rows={6}
              value={innovationLevel}
              onChange={(e) => setInnovationLevel(e.target.value)}
            />
          </Grid>
        </Grid>

        {/* Botões Guardar e Partilhar */}
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
          >
            Save
          </Button>
        </Box>
      </Container>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <Box>
        <Footer />
      </Box>
    </Box>
  );
}